import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { RefreshCw, X, Plus, Loader2, Download, KeyRound, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { AboutEditor } from "../components/AboutEditor";

function fmtDate(iso) {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

function relTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "unknown";
  const diff = Date.now() - then;
  if (diff < 60_000) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

const ChannelSourceCard = ({ channel, onAdd, onRemove, onSync, onTopic }) => {
  const [newHandle, setNewHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const isStale = !channel.fetched_at || Date.now() - new Date(channel.fetched_at).getTime() > DAY_MS + 60 * 60 * 1000;

  const add = async () => {
    if (!newHandle.trim()) return;
    setBusy(true);
    await onAdd(channel.slug, newHandle.trim());
    setNewHandle("");
    setBusy(false);
  };

  return (
    <div
      data-testid={`admin-channel-${channel.slug}`}
      className="bg-[#0A0A0F] border border-white/10 rounded-sm p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-2xl uppercase tracking-tight">{channel.name}</h3>
          <p
            className="flex items-center gap-1.5 text-xs text-zinc-500 mt-1 tabular-nums"
            data-testid={`admin-synced-${channel.slug}`}
            title={`Last synced: ${fmtDate(channel.fetched_at)}`}
          >
            <span
              className={`inline-block w-2 h-2 rounded-full ${isStale ? "bg-amber-500" : "bg-emerald-500"}`}
            />
            {channel.video_count} videos · last synced {relTime(channel.fetched_at)}
            {isStale && <span className="text-amber-500 uppercase tracking-wide ml-1">· stale</span>}
          </p>
        </div>
        <button
          data-testid={`admin-sync-${channel.slug}`}
          onClick={() => onSync(channel.slug)}
          className="flex items-center gap-1.5 text-xs border border-white/20 hover:bg-white/10 px-3 py-2 rounded-sm transition-colors shrink-0"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {channel.handles.length === 0 && (
          <span className="text-xs text-zinc-600">No creators yet.</span>
        )}
        {channel.handles.map((h) => (
          <span
            key={h}
            data-testid={`admin-handle-${channel.slug}-${h}`}
            className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-sm pl-3 pr-2 py-1.5 text-sm"
          >
            @{h}
            {channel.topics && (
              <select
                data-testid={`admin-topic-${channel.slug}-${h}`}
                aria-label={`Row for ${h}`}
                value={channel.topics[h] || channel.topic_options[0]}
                onChange={(e) => onTopic(channel.slug, h, e.target.value)}
                className="bg-[#030305] border border-white/15 rounded-sm px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]"
              >
                {channel.topic_options.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}
            <button
              data-testid={`admin-remove-${channel.slug}-${h}`}
              onClick={() => onRemove(channel.slug, h)}
              className="text-zinc-500 hover:text-[#FF2A2A] transition-colors"
              aria-label={`Remove ${h}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <span className="text-zinc-500 text-sm">@</span>
        <input
          data-testid={`admin-add-input-${channel.slug}`}
          value={newHandle}
          onChange={(e) => setNewHandle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="creator-handle"
          className="flex-1 bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A] transition"
        />
        <button
          data-testid={`admin-add-btn-${channel.slug}`}
          onClick={add}
          disabled={busy}
          className="flex items-center gap-1 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-3 py-2 rounded-sm text-sm font-medium transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </button>
      </div>
      <AboutEditor slug={channel.slug} initial={channel.about} />
    </div>
  );
};

const SponsorsManager = ({ channels }) => {
  const [sponsors, setSponsors] = useState(null);
  const empty = { channel: "all", title: "", url: "", description: "", image_url: "", cta_text: "Learn More" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/sponsors");
      setSponsors(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load sponsors");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      toast.error("Title and URL are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/sponsors", { ...form, active: true });
      toast.success("Sponsor added.");
      setForm(empty);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add sponsor");
    }
    setBusy(false);
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/sponsors/${s.id}`, { ...s, active: !s.active });
      await load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/sponsors/${id}`);
      toast.success("Sponsor removed.");
      await load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const channelName = (slug) =>
    slug === "all" ? "All channels (site-wide)" : (channels.find((c) => c.slug === slug)?.name || slug);

  return (
    <div className="mt-16" data-testid="sponsors-section">
      <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mb-2">Sponsors &amp; Affiliate Links</h2>
      <p className="text-sm text-zinc-500 mb-8">
        Promo cards shown in a "Sponsored" strip on each channel page. Use "All channels" for a site-wide link. Clicks are tracked below.
      </p>

      <div className="bg-[#0A0A0F] border border-white/10 rounded-sm p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Channel</label>
            <select
              data-testid="admin-sponsor-channel"
              value={form.channel}
              onChange={set("channel")}
              className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]"
            >
              <option value="all">All channels (site-wide)</option>
              {channels.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Button text</label>
            <input data-testid="admin-sponsor-cta" value={form.cta_text} onChange={set("cta_text")} placeholder="Shop Now" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Title *</label>
            <input data-testid="admin-sponsor-title" value={form.title} onChange={set("title")} placeholder="Sponsor / product name" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Link URL *</label>
            <input data-testid="admin-sponsor-url" value={form.url} onChange={set("url")} placeholder="https://your-affiliate-link.com" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Image URL</label>
            <input data-testid="admin-sponsor-image" value={form.image_url} onChange={set("image_url")} placeholder="https://logo-or-banner.jpg" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Description</label>
            <input data-testid="admin-sponsor-desc" value={form.description} onChange={set("description")} placeholder="One short line about the offer" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
        </div>
        <button
          data-testid="admin-sponsor-add-btn"
          onClick={create}
          disabled={busy}
          className="mt-5 flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Sponsor
        </button>
      </div>

      {sponsors === null ? (
        <div className="text-zinc-500 text-sm">Loading sponsors...</div>
      ) : sponsors.length === 0 ? (
        <div className="text-zinc-500 text-sm border border-white/10 rounded-sm p-8 text-center" data-testid="sponsors-empty">
          No sponsors yet. Add your first affiliate or sponsor link above.
        </div>
      ) : (
        <div className="space-y-3">
          {sponsors.map((s) => (
            <div key={s.id} data-testid={`admin-sponsor-row-${s.id}`} className="flex items-center gap-4 bg-[#0A0A0F] border border-white/10 rounded-sm p-4">
              {s.image_url ? <img src={s.image_url} alt="" className="w-14 h-14 object-cover rounded-sm shrink-0" /> : <div className="w-14 h-14 rounded-sm bg-white/5 shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white truncate">{s.title}</p>
                <p className="text-xs text-zinc-500 truncate">{channelName(s.channel)} · {s.url}</p>
              </div>
              <div className="text-center shrink-0 px-2">
                <p className="font-display text-2xl tabular-nums text-white">{s.impressions}</p>
                <p className="text-[10px] uppercase tracking-wide text-zinc-500">views</p>
              </div>
              <div className="text-center shrink-0 px-2">
                <p className="font-display text-2xl tabular-nums text-[#FF2A2A]">{s.clicks}</p>
                <p className="text-[10px] uppercase tracking-wide text-zinc-500">clicks</p>
              </div>
              <div className="text-center shrink-0 px-2">
                <p className="font-display text-2xl tabular-nums text-white">{s.ctr}%</p>
                <p className="text-[10px] uppercase tracking-wide text-zinc-500">CTR</p>
              </div>
              <button
                data-testid={`admin-sponsor-toggle-${s.id}`}
                onClick={() => toggleActive(s)}
                className={`text-xs px-3 py-1.5 rounded-sm border transition-colors shrink-0 ${s.active ? "border-[#FF2A2A] text-[#FF2A2A]" : "border-white/20 text-zinc-500"}`}
              >
                {s.active ? "Active" : "Hidden"}
              </button>
              <button
                data-testid={`admin-sponsor-delete-${s.id}`}
                onClick={() => remove(s.id)}
                className="text-zinc-500 hover:text-[#FF2A2A] transition-colors shrink-0"
                aria-label="Delete sponsor"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InstagramManager = ({ channels }) => {
  const [reels, setReels] = useState(null);
  const defaultChannel = channels.find((c) => c.slug === "qstyleshorts") ? "qstyleshorts" : channels[0]?.slug;
  const [form, setForm] = useState({ channel: defaultChannel, url: "", title: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/instagram");
      setReels(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load reels");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async () => {
    if (!form.url.trim()) {
      toast.error("Paste an Instagram reel URL.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/instagram", form);
      toast.success("Reel added to the channel.");
      setForm((f) => ({ ...f, url: "", title: "" }));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add reel");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/instagram/${id}`);
      toast.success("Reel removed.");
      await load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const channelName = (slug) => channels.find((c) => c.slug === slug)?.name || slug;

  return (
    <div className="mt-16" data-testid="instagram-section">
      <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mb-2">Instagram Reels</h2>
      <p className="text-sm text-zinc-500 mb-8">
        Paste a public Instagram reel/post link to feature it on a channel. It shows in a vertical "From Instagram" player. No account connection needed.
      </p>

      <div className="bg-[#0A0A0F] border border-white/10 rounded-sm p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Channel</label>
            <select
              data-testid="admin-ig-channel"
              value={form.channel}
              onChange={set("channel")}
              className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]"
            >
              {channels.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Caption (optional)</label>
            <input data-testid="admin-ig-title" value={form.title} onChange={set("title")} placeholder="Short caption shown on the reel" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Instagram Reel URL *</label>
            <input data-testid="admin-ig-url" value={form.url} onChange={set("url")} placeholder="https://www.instagram.com/reel/XXXXXXXXX/" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
        </div>
        <button
          data-testid="admin-ig-add-btn"
          onClick={add}
          disabled={busy}
          className="mt-5 flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Reel
        </button>
      </div>

      {reels === null ? (
        <div className="text-zinc-500 text-sm">Loading reels...</div>
      ) : reels.length === 0 ? (
        <div className="text-zinc-500 text-sm border border-white/10 rounded-sm p-8 text-center" data-testid="instagram-empty">
          No reels yet. Paste your first Instagram reel link above.
        </div>
      ) : (
        <div className="space-y-3">
          {reels.map((r) => (
            <div key={r.id} data-testid={`admin-ig-row-${r.id}`} className="flex items-center gap-4 bg-[#0A0A0F] border border-white/10 rounded-sm p-4">
              {r.thumbnail_url ? (
                <img src={r.thumbnail_url} alt="" className="w-12 h-16 object-cover rounded-sm shrink-0" />
              ) : (
                <div className="w-12 h-16 rounded-sm bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white truncate">{r.title || r.oembed_title || "Instagram Reel"}</p>
                <p className="text-xs text-zinc-500 truncate">{channelName(r.channel)} · {r.url}</p>
              </div>
              <button
                data-testid={`admin-ig-delete-${r.id}`}
                onClick={() => remove(r.id)}
                className="text-zinc-500 hover:text-[#FF2A2A] transition-colors shrink-0"
                aria-label="Delete reel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TIKTOK_SECTIONS = ["Women's Fashion", "Music & Dance", "Funny", "Travel", "Fitness", "Automotive", "Outdoor Life", "Lifestyle", "Philadelphia", "Sports"];

const TikTokManager = ({ channels }) => {
  const [reels, setReels] = useState(null);
  const defaultChannel = channels.find((c) => c.slug === "qtvtiktok") ? "qtvtiktok" : channels[0]?.slug;
  const [form, setForm] = useState({ channel: defaultChannel, url: "", title: "", section: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/tiktok");
      setReels(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load TikToks");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async () => {
    if (!form.url.trim()) {
      toast.error("Paste a TikTok video URL.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/tiktok", form);
      toast.success("TikTok added to the channel.");
      setForm((f) => ({ ...f, url: "", title: "" }));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add TikTok");
    }
    setBusy(false);
  };

  const retag = async (id, section) => {
    try {
      await api.patch(`/admin/tiktok/${id}`, { section });
      setReels((rs) => rs.map((r) => (r.id === id ? { ...r, section } : r)));
      toast.success(section ? `Moved to ${section}` : "Set to uncategorized");
    } catch (e) {
      toast.error("Failed to update section");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/tiktok/${id}`);
      toast.success("TikTok removed.");
      await load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const channelName = (slug) => channels.find((c) => c.slug === slug)?.name || slug;

  return (
    <div className="mt-16" data-testid="tiktok-section">
      <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mb-2">TikTok Videos</h2>
      <p className="text-sm text-zinc-500 mb-8">
        Paste a public TikTok link (including share links like tiktok.com/t/XXXX) to feature it in a vertical player. On QTV TikTok, pick a section so it shows under the right filter. No account connection needed.
      </p>

      <div className="bg-[#0A0A0F] border border-white/10 rounded-sm p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Channel</label>
            <select
              data-testid="admin-tt-channel"
              value={form.channel}
              onChange={set("channel")}
              className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]"
            >
              {channels.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Section (QTV TikTok)</label>
            <select
              data-testid="admin-tt-section"
              value={form.section}
              onChange={set("section")}
              className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]"
            >
              <option value="">Uncategorized (All)</option>
              {TIKTOK_SECTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Caption (optional)</label>
            <input data-testid="admin-tt-title" value={form.title} onChange={set("title")} placeholder="Short caption shown on the video" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">TikTok URL *</label>
            <input data-testid="admin-tt-url" value={form.url} onChange={set("url")} placeholder="https://www.tiktok.com/t/XXXXXXXXX/" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
        </div>
        <button
          data-testid="admin-tt-add-btn"
          onClick={add}
          disabled={busy}
          className="mt-5 flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add TikTok
        </button>
      </div>

      {reels === null ? (
        <div className="text-zinc-500 text-sm">Loading TikToks...</div>
      ) : reels.length === 0 ? (
        <div className="text-zinc-500 text-sm border border-white/10 rounded-sm p-8 text-center" data-testid="tiktok-empty">
          No TikToks yet. Paste your first TikTok link above.
        </div>
      ) : (
        <div className="space-y-3">
          {reels.map((r) => (
            <div key={r.id} data-testid={`admin-tt-row-${r.id}`} className="flex items-center gap-4 bg-[#0A0A0F] border border-white/10 rounded-sm p-4">
              {r.thumbnail_url ? (
                <img src={r.thumbnail_url} alt="" className="w-12 h-16 object-cover rounded-sm shrink-0" />
              ) : (
                <div className="w-12 h-16 rounded-sm bg-gradient-to-br from-[#25F4EE] via-black to-[#FE2C55] shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white truncate">{r.title || r.oembed_title || "TikTok Video"}</p>
                <p className="text-xs text-zinc-500 truncate">{channelName(r.channel)} · {r.url}</p>
              </div>
              {r.channel === "qtvtiktok" && (
                <select
                  data-testid={`admin-tt-section-${r.id}`}
                  value={r.section || ""}
                  onChange={(e) => retag(r.id, e.target.value)}
                  className="bg-[#030305] border border-white/10 rounded-sm px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A] shrink-0"
                >
                  <option value="">Uncategorized</option>
                  {TIKTOK_SECTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
              <button
                data-testid={`admin-tt-delete-${r.id}`}
                onClick={() => remove(r.id)}
                className="text-zinc-500 hover:text-[#FF2A2A] transition-colors shrink-0"
                aria-label="Delete TikTok"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ChangeEmailManager = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ new_email: "", current_password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.new_email || !form.current_password) {
      toast.error("Enter your new email and current password.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-email", form);
      toast.success("Email updated. Use your new email to sign in next time.");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change email");
    }
    setBusy(false);
  };

  return (
    <div className="bg-[#0A0A0F] border border-white/10 rounded-sm p-6 max-w-xl mb-6" data-testid="change-email-card">
      <h3 className="font-semibold text-white mb-1">Login email</h3>
      <p className="text-xs text-zinc-500 mb-5">
        Current: <span className="text-zinc-300" data-testid="current-email">{user?.email || "—"}</span>. Use a real inbox so password-reset emails can reach you.
      </p>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide">New email</label>
          <input type="email" data-testid="admin-email-new" value={form.new_email} onChange={set("new_email")} placeholder="you@yourdomain.com" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
        </div>
        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wide">Current password (to confirm)</label>
          <input type="password" data-testid="admin-email-password" value={form.current_password} onChange={set("current_password")} autoComplete="current-password" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
        </div>
      </div>
      <button
        data-testid="admin-email-submit"
        onClick={submit}
        disabled={busy}
        className="mt-5 flex items-center gap-2 bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update Email
      </button>
    </div>
  );
};

const ChangePasswordManager = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.current_password || !form.new_password) {
      toast.error("Fill in both password fields.");
      return;
    }
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed. Use your new password next time you sign in.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change password");
    }
    setBusy(false);
  };

  return (
    <div className="mt-16" data-testid="change-password-section">
      <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mb-2 flex items-center gap-3">
        <KeyRound className="w-8 h-8 text-[#FF2A2A]" /> Account Security
      </h2>
      <p className="text-sm text-zinc-500 mb-8">Change your admin login email and password. Changes take effect immediately and survive redeploys.</p>

      <ChangeEmailManager />

      <div className="bg-[#0A0A0F] border border-white/10 rounded-sm p-6 max-w-xl">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Current password</label>
            <input type="password" data-testid="admin-pw-current" value={form.current_password} onChange={set("current_password")} autoComplete="current-password" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">New password (min 6 characters)</label>
            <input type="password" data-testid="admin-pw-new" value={form.new_password} onChange={set("new_password")} autoComplete="new-password" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wide">Confirm new password</label>
            <input type="password" data-testid="admin-pw-confirm" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" className="mt-1 w-full bg-[#030305] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]" />
          </div>
        </div>
        <button
          data-testid="admin-pw-submit"
          onClick={submit}
          disabled={busy}
          className="mt-5 flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update Password
        </button>
      </div>
    </div>
  );
};

const LeadsManager = () => {
  const [leads, setLeads] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/leads");
      setLeads(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load leads");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      await load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const exportCsv = () => {
    if (!leads || leads.length === 0) return;
    const header = ["Date", "Channel", "Name", "Email", "Interest", "Message"];
    const esc = (c) => `"${String(c || "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) => [l.created_at, l.channel, l.name, l.email, l.interest, l.message].map(esc).join(","));
    const csv = [header.map(esc).join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "qtv-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-16" data-testid="leads-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mb-2">Leads</h2>
          <p className="text-sm text-zinc-500">
            Captured from the Living Abroad, RV &amp; Business sign-up forms. {leads ? `${leads.length} total.` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
        <button
          data-testid="weekly-preview-btn"
          onClick={async () => {
            try {
              const r = await api.post("/admin/weekly-preview");
              toast.success(`Weekly highlights preview sent to ${r.data.to}`);
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send preview");
            }
          }}
          className="flex items-center gap-2 border border-white/20 hover:bg-white/10 text-white px-5 py-2.5 rounded-sm text-sm font-medium transition-colors"
        >
          <Send className="w-4 h-4" /> Email me the weekly preview
        </button>
        <button
          data-testid="leads-export-btn"
          onClick={exportCsv}
          disabled={!leads || leads.length === 0}
          className="flex items-center gap-2 border border-white/20 hover:bg-white/10 disabled:opacity-50 text-white px-5 py-2.5 rounded-sm text-sm font-medium transition-colors"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
        </div>
      </div>

      {leads === null ? (
        <div className="text-zinc-500 text-sm">Loading leads...</div>
      ) : leads.length === 0 ? (
        <div className="text-zinc-500 text-sm border border-white/10 rounded-sm p-8 text-center" data-testid="leads-empty">
          No leads yet. They'll appear here when visitors submit the sign-up form.
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} data-testid={`admin-lead-row-${l.id}`} className="flex items-start gap-4 bg-[#0A0A0F] border border-white/10 rounded-sm p-4">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white">{l.name} <span className="text-zinc-500 font-normal">· {l.email}</span></p>
                <p className="text-xs text-zinc-500 mt-0.5 uppercase tracking-wide">{l.channel} · {fmtDate(l.created_at)}</p>
                {l.interest ? <p className="text-xs text-[#FF2A2A] mt-1" data-testid={`admin-lead-interest-${l.id}`}>Interested in: {l.interest}</p> : null}
                {l.message ? <p className="text-sm text-zinc-400 mt-2">{l.message}</p> : null}
              </div>
              <button
                data-testid={`admin-lead-delete-${l.id}`}
                onClick={() => remove(l.id)}
                className="text-zinc-500 hover:text-[#FF2A2A] transition-colors shrink-0"
                aria-label="Delete lead"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [data, setData] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/sources");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addHandle = async (slug, handle) => {
    try {
      await api.post(`/admin/sources/${slug}`, { handle });
      toast.success(`Added @${handle}. Hit Refresh to pull their videos.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add");
    }
  };

  const removeHandle = async (slug, handle) => {
    try {
      await api.delete(`/admin/sources/${slug}/${handle}`);
      toast.success(`Removed @${handle}.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to remove");
    }
  };

  const setTopic = async (slug, handle, topic) => {
    try {
      await api.put(`/admin/topics/${slug}`, { handle, topic });
      toast.success(`@${handle} now shows in the ${topic} row.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update row");
    }
  };

  const syncChannel = async (slug) => {
    toast.loading(`Refreshing ${slug}...`, { id: `sync-${slug}` });
    try {
      const res = await api.post(`/admin/sync/${slug}`);
      toast.success(`${slug}: pulled ${res.data.count} videos.`, { id: `sync-${slug}` });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed", { id: `sync-${slug}` });
    }
  };

  const syncAll = async () => {
    setRefreshingAll(true);
    toast.loading("Refreshing all channels in the background...", { id: "sync-all" });
    try {
      await api.post("/admin/sync");
      // Poll status a few times to reflect progress
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        await load();
        if (tries >= 6) {
          clearInterval(poll);
          setRefreshingAll(false);
          toast.success("Refresh complete.", { id: "sync-all" });
        }
      }, 5000);
    } catch (e) {
      setRefreshingAll(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed", { id: "sync-all" });
    }
  };

  return (
    <div className="min-h-screen bg-[#030305]">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Content Admin</p>
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter">Channel Sources</h1>
            <p className="text-sm text-zinc-500 mt-3">
              Last full sync: {fmtDate(data?.last_synced)} · Auto-refreshes daily
            </p>
          </div>
          <button
            data-testid="admin-refresh-all"
            onClick={syncAll}
            disabled={refreshingAll}
            className="flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
          >
            {refreshingAll ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
            Refresh All Now
          </button>
        </div>

        {!data ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-[#FF2A2A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start"
          >
            {data.channels.map((ch) => (
              <ChannelSourceCard
                key={ch.slug}
                channel={ch}
                onAdd={addHandle}
                onRemove={removeHandle}
                onSync={syncChannel}
                onTopic={setTopic}
              />
            ))}
          </motion.div>
        )}

        {data && <SponsorsManager channels={data.channels} />}
        {data && <InstagramManager channels={data.channels} />}
        {data && <TikTokManager channels={data.channels} />}
        {data && <LeadsManager />}
        {data && <ChangePasswordManager />}
      </div>
    </div>
  );
}
